import { ref, watch } from 'vue';
import { useCustomerStore } from '../store/customer';
import { isMessageUnread } from './messageStatus';

/**
 * Shared load/read-tracking behaviour for MessagesPage.vue and ClaimMessagesPage.vue.
 *
 * `getMessages` must return this page's current relevant messages (each already embedding
 * `.claim`, as both pages already build them) - used both to mark messages read and to derive
 * the "New" badge set.
 *
 * ion-router-outlet keeps these pages' component instances alive across navigation, so a plain
 * onMounted only ever fires once per app session for a given route - a second visit (including a
 * fresh login without a full page reload) would otherwise reuse stale isLoading/hasLoaded/
 * newMessageIds state from the very first visit. Callers should instead run `load` from
 * onIonViewWillEnter, which fires every time the page becomes active again.
 *
 * `load` guards against re-entrancy: if the customer navigates away and back before a call's
 * fetch resolves, an older call finishing after a newer one is ignored rather than clobbering the
 * newer call's state.
 */
export function useUnreadMessages() {
  const customerStore = useCustomerStore();
  const isLoading = ref(true);
  const hasLoaded = ref(false);
  const newMessageIds = ref([]);
  let requestToken = 0;
  let stopWatchingMessages = null;

  /**
   * Marks any not-yet-seen unread message read and adds it to newMessageIds. Deliberately additive
   * rather than a one-time snapshot - a message that arrives mid-visit (e.g. via NavigationMenu's
   * background bell refresh) should still show "New" for the rest of this visit, not just whatever
   * was already unread the moment the page first loaded.
   */
  function syncUnread(messages) {
    // message.claim can briefly be missing - ClaimMessagesPage.vue's messages/claims fetches run
    // in parallel, and this can react to a partial update where messages have landed but claims
    // haven't yet. Skipped here rather than crashing; the watch fires again once claims lands.
    const unseen = messages.filter(
      (message) =>
        message.claim && isMessageUnread(message) && !newMessageIds.value.includes(message.id)
    );
    if (unseen.length === 0) return;

    newMessageIds.value = [...newMessageIds.value, ...unseen.map((message) => message.id)];
    unseen.forEach((message) => customerStore.markMessageRead(message.claim.id, message.id));
  }

  /**
   * Runs `fetch` (the page's own per-visit fetch, resolving to whether it fully succeeded), then
   * syncs read state from `getMessages()` and keeps doing so reactively for the rest of this
   * visit. Call from onIonViewWillEnter, paired with stopTracking() from onIonViewWillLeave -
   * ion-router-outlet keeps a page's component instance alive (Vue's activated/deactivated
   * semantics) rather than unmounting it, so a watcher set up once at setup-time would keep
   * running - and keep marking newly-arrived messages read - even while the customer has
   * navigated away and isn't looking at this page at all.
   */
  async function load(fetch, getMessages) {
    const token = ++requestToken;
    isLoading.value = true;
    hasLoaded.value = false;
    newMessageIds.value = [];

    stopTracking();
    stopWatchingMessages = watch(getMessages, syncUnread);

    const loaded = await fetch();
    if (token !== requestToken) return;

    isLoading.value = false;
    hasLoaded.value = loaded;
    syncUnread(getMessages());
  }

  /** Stops reacting to message changes. Call from onIonViewWillLeave. */
  function stopTracking() {
    if (stopWatchingMessages) {
      stopWatchingMessages();
      stopWatchingMessages = null;
    }
  }

  return { isLoading, hasLoaded, newMessageIds, load, stopTracking };
}
